/**
 * O formulário da nova organização com contrato (#765, fase 4, #769), sem tela.
 *
 * ── O formato que a tela pede ─────────────────────────────────────────────
 *
 * A criação passa a pedir cadastro, contrato e contato financeiro, e não
 * aceita mais plano em texto livre. `NovaOrganizacaoComContrato` é o formato
 * que a tela pede ao contrato de `POST /v1/operacao/revendas`, que cresce sem
 * quebrar (os campos de hoje continuam aceitos). A tela nasceu antes do back
 * (decisão de 26/10/2026).
 *
 * A conferência daqui é de formato. Se o CNPJ já é de outra organização e se
 * o identificador está livre, quem sabe é o servidor.
 */

import { emMicros } from '@/shared/lib/formato';
import { cnpjSemMascara, cnpjValido } from '@/shared/lib/cnpj';
import { EMAIL_VALIDO, SLUG_VALIDO } from './regras-dos-campos';

/** Dinheiro como o contrato manda: micros, para centavo não acumular erro. */
export type Dinheiro = { readonly quantia_micros: number; readonly moeda: string };

/** Um plano do catálogo, como a escolha do plano o mostra. Só os ativos. */
export type PlanoParaEscolher = {
  readonly id: string;
  readonly nome: string;
  readonly mensalidade: Dinheiro | null;
  readonly usuariosIncluidos: number;
  readonly armazenamentoGb: number;
  readonly usuarioAdicional: Dinheiro | null;
  readonly gbAdicional: Dinheiro | null;
};

export type NovaOrganizacaoComContrato = {
  readonly razaoSocial: string;
  readonly nomeFantasia: string;
  /** Sem a máscara, com as letras do CNPJ alfanumérico. */
  readonly cnpj: string;
  readonly segmento: string;
  readonly identificador: string;
  readonly fuso: string;
  readonly planoId: string;
  readonly usuariosContratados: number;
  readonly usuarioAdicional: Dinheiro | null;
  readonly armazenamentoContratadoGb: number;
  readonly gbAdicional: Dinheiro | null;
  /** AAAA-MM-DD. */
  readonly inicioDoContrato: string;
  /** Telefone e contato financeiro são dado pessoal: nunca em log. */
  readonly administrador: {
    readonly nome: string;
    readonly email: string;
    readonly telefone: string | null;
  };
  readonly financeiro: { readonly nome: string; readonly email: string; readonly telefone: string };
};

/** O que a pessoa digitou, campo a campo, como texto. */
export type CamposDoFormulario = {
  readonly razaoSocial: string;
  readonly nomeFantasia: string;
  readonly cnpj: string;
  readonly segmento: string;
  readonly identificador: string;
  readonly fuso: string;
  readonly planoId: string;
  readonly usuarios: string;
  readonly adicional: string;
  readonly armazenamento: string;
  readonly gbAdicional: string;
  readonly inicio: string;
  readonly nomeDoAdministrador: string;
  readonly emailDoAdministrador: string;
  readonly telefoneDoAdministrador: string;
  readonly nomeDoFinanceiro: string;
  readonly emailDoFinanceiro: string;
  readonly telefoneDoFinanceiro: string;
};

export type ErrosDoFormulario = Partial<Record<keyof CamposDoFormulario, string>>;

const OBRIGATORIO = 'Preencha este campo.';
const CURTO = 'Escreva pelo menos 2 letras.';
const TELEFONE = 'Escreva o telefone com o DDD, como (11) 98765-4321.';
const INTEIRO = 'Escreva um número inteiro, a partir de 1.';
const DINHEIRO = 'Escreva o valor em reais, como 49,90.';
const EMAIL = 'Escreva um e-mail completo, como nome@empresa.com.br.';
const DATA = /^\d{4}-\d{2}-\d{2}$/;

function inteiroPositivo(texto: string): number | null {
  const limpo = texto.trim();
  if (!/^\d+$/.test(limpo)) return null;
  const numero = Number(limpo);
  return numero >= 1 ? numero : null;
}

function dinheiroOpcional(texto: string, moeda: string): Dinheiro | null | undefined {
  if (texto.trim() === '') return null;
  const micros = emMicros(texto);
  return micros === null ? undefined : { quantia_micros: micros, moeda };
}

/** O formulário inteiro vira o pedido, ou o motivo de cada campo que não serve. */
export function lerFormulario(
  campos: CamposDoFormulario,
  moeda = 'BRL',
):
  | { readonly ok: true; readonly dados: NovaOrganizacaoComContrato }
  | { readonly ok: false; readonly erros: ErrosDoFormulario } {
  const erros: ErrosDoFormulario = {};
  // O mínimo é o do servidor: nome e razão social com pelo menos 2 letras.
  const exigir = (campo: keyof CamposDoFormulario, minimo = 1) => {
    const texto = campos[campo].trim();
    if (texto === '') erros[campo] = OBRIGATORIO;
    else if (texto.length < minimo) erros[campo] = CURTO;
  };
  // O telefone, com o DDD: quem confere se ele existe é o servidor.
  const telefone = (campo: keyof CamposDoFormulario) => {
    if (campos[campo].replace(/\D/g, '').length < 10) erros[campo] = TELEFONE;
  };

  exigir('razaoSocial', 2);
  exigir('nomeFantasia', 2);
  if (!cnpjValido(campos.cnpj)) {
    erros.cnpj = campos.cnpj.trim() === '' ? OBRIGATORIO : 'Confira o CNPJ: os dígitos não batem.';
  }
  if (campos.segmento === '') erros.segmento = 'Escolha o segmento.';
  if (!SLUG_VALIDO.test(campos.identificador)) {
    erros.identificador = 'Use de 3 a 32 letras minúsculas, números ou hífen.';
  }
  if (campos.fuso === '') erros.fuso = 'Escolha o fuso.';
  if (campos.planoId === '') erros.planoId = 'Escolha um plano do catálogo.';

  const usuarios = inteiroPositivo(campos.usuarios);
  if (usuarios === null) erros.usuarios = INTEIRO;
  const armazenamento = inteiroPositivo(campos.armazenamento);
  if (armazenamento === null) erros.armazenamento = INTEIRO;
  const adicional = dinheiroOpcional(campos.adicional, moeda);
  if (adicional === undefined) erros.adicional = DINHEIRO;
  const gbAdicional = dinheiroOpcional(campos.gbAdicional, moeda);
  if (gbAdicional === undefined) erros.gbAdicional = DINHEIRO;
  if (!DATA.test(campos.inicio) || Number.isNaN(Date.parse(campos.inicio))) {
    erros.inicio = 'Escolha a data de início do contrato.';
  }

  exigir('nomeDoAdministrador', 2);
  if (!EMAIL_VALIDO.test(campos.emailDoAdministrador.trim())) erros.emailDoAdministrador = EMAIL;
  if (campos.telefoneDoAdministrador.trim() !== '') telefone('telefoneDoAdministrador');
  exigir('nomeDoFinanceiro', 2);
  if (!EMAIL_VALIDO.test(campos.emailDoFinanceiro.trim())) erros.emailDoFinanceiro = EMAIL;
  if (campos.telefoneDoFinanceiro.trim() === '') erros.telefoneDoFinanceiro = OBRIGATORIO;
  else telefone('telefoneDoFinanceiro');

  if (
    Object.keys(erros).length > 0 ||
    usuarios === null ||
    armazenamento === null ||
    adicional === undefined ||
    gbAdicional === undefined
  ) {
    return { ok: false, erros };
  }

  return {
    ok: true,
    dados: {
      razaoSocial: campos.razaoSocial.trim(),
      nomeFantasia: campos.nomeFantasia.trim(),
      cnpj: cnpjSemMascara(campos.cnpj),
      segmento: campos.segmento,
      identificador: campos.identificador,
      fuso: campos.fuso,
      planoId: campos.planoId,
      usuariosContratados: usuarios,
      usuarioAdicional: adicional,
      armazenamentoContratadoGb: armazenamento,
      gbAdicional,
      inicioDoContrato: campos.inicio,
      administrador: {
        nome: campos.nomeDoAdministrador.trim(),
        email: campos.emailDoAdministrador.trim(),
        telefone: campos.telefoneDoAdministrador.trim() || null,
      },
      financeiro: {
        nome: campos.nomeDoFinanceiro.trim(),
        email: campos.emailDoFinanceiro.trim(),
        telefone: campos.telefoneDoFinanceiro.trim(),
      },
    },
  };
}

/** O dinheiro como a pessoa digita: "49,90". */
function emReais(valor: Dinheiro | null): string {
  if (!valor) return '';
  return (valor.quantia_micros / 1_000_000).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Os valores do contrato que vêm do plano escolhido, prontos para os campos. */
export function valoresDoPlano(
  plano: PlanoParaEscolher,
): Pick<CamposDoFormulario, 'usuarios' | 'adicional' | 'armazenamento' | 'gbAdicional'> {
  return {
    usuarios: String(plano.usuariosIncluidos),
    adicional: emReais(plano.usuarioAdicional),
    armazenamento: String(plano.armazenamentoGb),
    gbAdicional: emReais(plano.gbAdicional),
  };
}
